import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams, useNavigationType } from 'react-router-dom';
import { Loader2, Play, X, Heart, RotateCcw, Share2, Check, Flame, Volume2, VolumeX, Eye, Calendar, MessageCircle, ChevronRight, Highlighter, Type, Sparkles } from 'lucide-react';
import api, { BACKEND_URL, participatePrayer, leavePrayer, getParticipants, lightCandle, getCandleStatus, recordPrayerView, listOwnerComments, markSharedOpen, fetchLiveCounts, recordPrayerWatch, sendPrayerHeart, reportAudioFailure, getImmersiveSettings } from '../lib/api';
import { sharePrayer } from '../lib/share';
import { useAuth } from '../contexts/AuthContext';
import { getAudioEnvironment } from '../lib/audioEnvironment';
import { unlockIOSAudioSync, needsIOSUnlock } from '../lib/iosAudioUnlock';
import FloatingHearts from '../components/FloatingHearts';
import JoinCircleButton from '../components/JoinCircleButton';
import ReportPrayerButton from '../components/ReportPrayerButton';
import { bgGradient, readableTextOn, readableTextShadow } from '../lib/color';
import {
  fmtDur, fmtCount, fmtLongDate,
  tokenize, buildPaceProfile,
  PRAYER_HIGHLIGHT_BOX_STYLE,
} from './watch/pace';
import PoemText from './watch/PoemText';
import Sparks from './watch/Sparks';
import ImmersiveEffect, { EFFECT_STYLES, EFFECT_LABELS } from './watch/Effects';


const WatchPrayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, refresh: refreshAuth } = useAuth();
  const [prayer, setPrayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTranscript, setShowTranscript] = useState(true);

  // Immersive overlay state
  const [praying, setPraying] = useState(false);
  const [countdown, setCountdown] = useState(0); // 3 -> 2 -> 1 -> 0 = show prayer
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  // Site-wide sparks speed (admin-managed, public read).
  const [sparksSpeed, setSparksSpeed] = useState(1);
  useEffect(() => {
    getImmersiveSettings().then((d) => setSparksSpeed(d?.sparks_speed || 1)).catch(() => {});
  }, []);
  // TEMPORARY effect-style tryout: initial style from ?effect= URL param
  // (sparks | orbs | beams | ripples | none); the overlay's cycle button
  // flips through them live.
  const [effectStyle, setEffectStyle] = useState(() => {
    const p = new URLSearchParams(window.location.search).get('effect');
    return EFFECT_STYLES.includes(p) ? p : 'sparks';
  });
  const cycleEffect = () => {
    setEffectStyle((cur) => EFFECT_STYLES[(EFFECT_STYLES.indexOf(cur) + 1) % EFFECT_STYLES.length]);
  };
  const [hearts, setHearts] = useState([]);
  const [shareStatus, setShareStatus] = useState(null); // 'shared' | 'copied' | 'error' | null
  const shareTimerRef = useRef(null);
  // Candles: locally-tracked count + is-lit state. Both start from the
  // prayer's persisted totals and the ledger, then flip optimistically on
  // click (with rollback on failure). `candleJustLit` is a short-lived
  // pulse cue that fires only on a fresh light (not on un-light).
  const [candlesCount, setCandlesCount] = useState(0);
  const [isCandleLit, setIsCandleLit] = useState(false);
  const [candleLighting, setCandleLighting] = useState(false);
  const [candleJustLit, setCandleJustLit] = useState(false);
  const candleTimerRef = useRef(null);
  const [playbackEnded, setPlaybackEnded] = useState(false);
  // When narrationMode === 'system', clicking "Pray this now" first fetches
  // the TTS audio so the countdown doesn't start against a silent overlay.
  // While the fetch is in flight we show a small "generating voice" modal.
  const [preparingVoice, setPreparingVoice] = useState(false);

  // Transient toast shown when the viewer's narration setting is
  // "author only" but the owner disabled using their recording for this
  // prayer (prayer.is_silent === true). Removed per product request —
  // the toast was too intrusive on the countdown moment.

  // Owner comments — shown inline on the preview page in DESC chronological
  // order. Replaces the old prayer-description block. Fetched fresh on
  // mount so the preview reflects the latest owner notes.
  const [ownerComments, setOwnerComments] = useState([]);
  const [ownerCommentsLoading, setOwnerCommentsLoading] = useState(false);

  // Perspective-aware display text. When the author saved the prayer in
  // 1st person, they also saved a 3rd-person alternate at
  // `prayer.third_person_text`. For non-owner viewers we surface that
  // alternate — everywhere the prayer text is rendered (preview block,
  // countdown reading view, word-highlighted playback) — so the reading
  // sounds natural when someone else prays it. The author themselves
  // always sees the original text they saved. Signed-out viewers are
  // treated as non-owner (they never authored anything in this browser
  // session). Falls back to `description` when no alternate exists.
  const viewerIsOwner = Boolean(user && prayer && user.user_id === prayer.user_id);
  const displayText = useMemo(() => {
    if (!prayer) return '';
    const original = prayer.description || '';
    if (
      prayer.perspective_type === 'first_person'
      && !viewerIsOwner
      && typeof prayer.third_person_text === 'string'
      && prayer.third_person_text.trim()
    ) {
      return prayer.third_person_text;
    }
    return original;
  }, [prayer, viewerIsOwner]);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setOwnerCommentsLoading(true);
    listOwnerComments(id)
      .then((data) => { if (!cancelled) setOwnerComments(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setOwnerComments([]); })
      .finally(() => { if (!cancelled) setOwnerCommentsLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  // "Currently participating" counter (polled every 5s)
  const [participantsCount, setParticipantsCount] = useState(0);
  // Live list of authenticated participants (anonymous are counted but omitted).
  const [participantsList, setParticipantsList] = useState([]);
  // Whether the click-to-reveal name list is currently visible under the
  // light-a-candle pill.
  const [showParticipantsList, setShowParticipantsList] = useState(false);

  const audioRef = useRef(null);
  const overlayContentRef = useRef(null);
  const wordRefs = useRef({});
  const countdownTimerRef = useRef(null);
  const simTimerRef = useRef(null);
  // rAF-driven karaoke highlighter — 60fps polling of audio.currentTime
  // beats the throttled `timeupdate` event (which browsers fire only
  // every ~250-500ms and causes cumulative highlight lag).
  const rafIdRef = useRef(null);
  const heartIdRef = useRef(0);
  const heartbeatIntervalRef = useRef(null);
  const participantsIntervalRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/prayers/${id}`);
        if (!cancelled) {
          setPrayer(data);
          setCandlesCount(Number(data?.candles_count) || 0);
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.status === 404 ? 'Prayer not found' : 'Failed to load prayer');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Hydrate the current viewer's candle state (lit or unlit) so a returning
  // user sees the icon in the right state on load without flicker. Runs
  // whenever the viewer identity or prayer id changes.
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    if (!user) {
      // Anonymous viewers can't have lit anything — reset any stale flag
      // (e.g. after logout on this page).
      setIsCandleLit(false);
      return undefined;
    }
    getCandleStatus(id)
      .then((res) => {
        if (cancelled || !res) return;
        setIsCandleLit(Boolean(res.is_lit));
        if (typeof res.candles_count === 'number') {
          setCandlesCount(res.candles_count);
        }
      })
      .catch(() => { /* silent — falls back to unlit which is a safe default */ });
    return () => { cancelled = true; };
  }, [id, user?.user_id]);

  // Prayed-count bump helper. Fired ONLY when the viewer explicitly
  // initiates prayer — either the "Pray Now Together" button
  // (`handlePrayNow`) or the "Pray again" replay button (`replayPrayer`).
  // Merely opening the Watch page no longer inflates the count. Server
  // still applies its 30s per-(viewer, prayer) dedupe as a safety net
  // against accidental double-clicks.
  //
  // Fires TWO server calls together so the visible "views" count on
  // the prayer AND the author-facing "Prayed for you (N)" notification
  // counter both track the same explicit user action:
  //   1) POST /prayers/{id}/view  → `plays_count` (+ history / feed
  //      relevance side-effects).
  //   2) POST /prayers/{id}/watch → creates or bumps the author's
  //      `direct_notifications` row (`meta.watch_count`). Backend
  //      skips self-watches and enforces uniqueness on
  //      `(user_id, type, action_id)`.
  const bumpPrayedCount = () => {
    if (!id) return;
    recordPrayerView(id)
      .then((res) => {
        if (res && typeof res.plays_count === 'number') {
          setPrayer((prev) => (prev ? { ...prev, plays_count: res.plays_count } : prev));
        }
      })
      .catch((e) => { console.error('view record failed', e); });
    recordPrayerWatch(id).catch((e) => {
      console.error('record watch notify failed', e);
    });
  };

  // Shared-link tracking. When the URL carries `?ref=share` (the marker
  // `sharePrayer` embeds in the copied/shared link), record the open so
  // this prayer surfaces on the Home "Shared" tab, then strip the marker
  // from the URL so the address bar stays clean and refresh doesn't
  // re-record. Anonymous viewers 401 quietly.
  //
  // The URL may ALSO carry `?shared_by=<user_id>` — the member id of the
  // person who generated this share link. We forward that id to the
  // backend so the sharer gets credit on the recipient's ledger row +
  // activity log, then strip it alongside `ref` for a clean address bar.
  const [searchParams, setSearchParams] = useSearchParams();
  const sharedRef = searchParams.get('ref') === 'share';
  const sharedBy = searchParams.get('shared_by') || null;
  // Fire the shared-open record when EITHER of the share markers is
  // present. Historically we required `?ref=share` to be present, but
  // some shares (older links, links pasted by users, or third-party
  // re-wraps) drop the `ref` marker while keeping `shared_by`. If we
  // see a sharer id we still want to attribute the open and light up
  // the "Shared" tab.
  //
  // Additionally, treat direct/external URL landings (typed URLs,
  // clicks from outside apps, bookmarks, hard refreshes) as anonymous
  // share opens so they surface on the "From Anonymous" tab even when
  // the URL carries no `?ref=share` or `?shared_by` markers. React
  // Router flags such fresh browser navigations as `POP`; in-app
  // <Link> clicks fire as `PUSH`, so card-click navigations stay out
  // of the ledger as before.
  const navigationType = useNavigationType();
  const isExternalLanding = navigationType === 'POP';
  const shouldRecordShareOpen = sharedRef || !!sharedBy || isExternalLanding;
  // Buffer the share attribution until the record actually succeeds. If
  // the viewer is anonymous the initial POST returns 401 (silent no-op),
  // and we DON'T strip the URL markers so a subsequent sign-in can
  // replay the record with the sharer id still attached. Once the
  // record lands (200), we strip the params for a clean address bar.
  const shareRecordedRef = useRef(false);
  useEffect(() => {
    if (!id || !shouldRecordShareOpen) return;
    if (shareRecordedRef.current) return;
    // While auth is still resolving, wait — otherwise an anonymous
    // POST would race the auth cookie hydration and silently 401.
    if (authLoading) return;
    // If we don't have a user yet, don't record — but ALSO don't
    // strip the URL params, so the effect can re-fire once the user
    // logs in via the auth modal (which flips `user` without a page
    // reload).
    if (!user) return;
    (async () => {
      const result = await markSharedOpen(
        id,
        sharedBy ? { shared_by: sharedBy } : {},
      ).catch((e) => {
        console.error('shared open record failed', e);
        return null;
      });
      if (result) {
        shareRecordedRef.current = true;
        const next = new URLSearchParams(searchParams);
        next.delete('ref');
        next.delete('shared_by');
        setSearchParams(next, { replace: true });
      }
    })();
  }, [id, shouldRecordShareOpen, sharedBy, user?.user_id, authLoading]);

  // Body scroll lock while overlay open
  useEffect(() => {
    if (!praying) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [praying]);

  // Cleanup timers on unmount
  useEffect(() => () => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    if (simTimerRef.current) clearInterval(simTimerRef.current);
    if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
    if (participantsIntervalRef.current) clearInterval(participantsIntervalRef.current);
  }, []);

  // Count only "speakable" words in the version of the text the viewer
  // actually sees (`displayText`) — the tokenizer skips @handles, matching
  // what the TTS narration reads out. Using displayText (not the raw
  // description) also keeps the pace math right for non-owners who watch
  // the 3rd-person alternate.
  const wordCount = useMemo(() => tokenize(displayText || '').wordCount, [displayText]);

  // `hasAudio` reflects whether the OWNER's recorded voice should be used
  // for narration. Even when a recording exists in storage, we treat it as
  // absent if the owner disabled "Use my voice recording for narration"
  // (persisted as `is_silent: true`) — viewers then fall back to their own
  // narration preference (system TTS or silence).
  const hasAudio = Boolean(prayer?.audio_url) && !prayer?.is_silent;

  // ---- Narration mode ----
  // Combines the viewer's stored preference with a session-scoped mute toggle
  // (mute icon on the watch screen). Resolves to one of:
  //   'author'  → play the author's recorded audio
  //   'system'  → play a system TTS voice for text-only prayers
  //   'silent'  → don't play any audio (still shows text)
  //
  // SAFETY-BY-DEFAULT: whenever we don't have a confirmed positive user pref
  // in hand (still loading, unknown value, anonymous viewer, /auth/me failed)
  // we return 'silent'. Never speculatively play. This is the "no hardcoded
  // play default" rule — audio only fires when we have EXPLICIT positive
  // confirmation that the viewer wants it.
  const [narrationMuted, setNarrationMuted] = useState(false);
  // Session-scoped toggle for whether the karaoke word highlighter is
  // active during the immersive overlay. Only meaningful when the
  // narration mode actually drives highlighting (author / system /
  // highlight_only). Resets to false on `closePraying` so each fresh
  // prayer session starts with highlighting on by default.
  const [highlightMuted, setHighlightMuted] = useState(false);
  // For SIGNED-IN viewers we use their saved preference; for ANONYMOUS
  // viewers we default to 'silent' ("No narration") — no audio and no
  // surprise cloud TTS calls until they sign in and opt in.
  const narrationPref = user?.narration_preference ?? (authLoading ? undefined : 'silent');

  // `prefReady` gates every audio-related decision. Only flips true once
  // AuthContext's own loading is done AND our watch-mount refetch has
  // completed. Until then narrationMode is forced to 'silent'.
  const [prefReady, setPrefReady] = useState(false);

  // Audio-blocking state. Two ways to reach `true`:
  //   1. Hostile environment detected on mount (in-app WebView, data
  //      saver, no HTMLMediaElement).
  //   2. Any `audio.play()` call rejects with NotAllowedError / similar
  //      at runtime — the muted user-gesture prime, the countdown-end
  //      kickoff, or the "Pray again" replay.
  // When true, `narrationMode` collapses to `'highlight_only'` and the
  // immersive overlay quietly shows a short "Silent mode — <reason>"
  // line with an "open in browser" affordance where meaningful.
  //
  // Once true within a Watch mount we DON'T flip it back to false —
  // recovering audio in the same session isn't worth the flicker of
  // narrationMode changing mid-immersion. A fresh page load re-runs
  // the detection.
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [audioBlockReason, setAudioBlockReason] = useState(null); // { reason, label, openInBrowserHref }
  const audioBlockedRef = useRef(false); // stable read from callbacks
  useEffect(() => { audioBlockedRef.current = audioBlocked; }, [audioBlocked]);

  // Detect a hostile audio environment on mount (in-app WebView, data
  // saver, etc.). Runs once — the browser doesn't change UA mid-page,
  // and `saveData` toggles are rare enough that a page-load re-check
  // is fine. Reports the detection to backend telemetry so admins can
  // spot patterns even for users who never tap "Pray Now Together".
  useEffect(() => {
    const env = getAudioEnvironment();
    if (env.hostile) {
      setAudioBlocked(true);
      setAudioBlockReason(env);
      reportAudioFailure({
        reason: env.reason,
        label: env.label,
        prayer_id: id,
        phase: 'pre_immersion',
        narration_pref: user?.narration_preference,
      });
    }
  }, [id, user?.narration_preference]);

  // Central downgrade helper. Callable from any audio failure path
  // (the muted prime, the countdown-end kick, the replay). Idempotent —
  // subsequent calls after the first are ignored for state updates but
  // still fire a telemetry ping so we can see cascade patterns.
  const handleAudioFailure = useCallback((phase, err) => {
    const message = (err && (err.message || String(err))) || 'play_rejected';
    reportAudioFailure({
      reason: 'play_rejected',
      label: phase,
      prayer_id: id,
      phase,
      error_message: message.slice(0, 200),
      narration_pref: user?.narration_preference,
    });
    if (audioBlockedRef.current) return;
    audioBlockedRef.current = true;
    setAudioBlocked(true);
    setAudioBlockReason({
      reason: 'play_rejected',
      label: 'Browser blocked audio',
      openInBrowserHref: typeof window !== 'undefined' ? window.location.href : '',
    });
  }, [id, user?.narration_preference]);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve(refreshAuth ? refreshAuth() : null)
      .catch(() => { /* non-critical, keep going */ })
      .finally(() => { if (!cancelled) setPrefReady(true); });
    return () => { cancelled = true; };
  }, [refreshAuth]);

  // (Removed) Prior effect reset the "owner did not record voice" toast
  // on URL id change. The toast itself was removed per product request.


  const narrationMode = useMemo(() => {
    // Session mute always wins, even before auth resolves.
    if (narrationMuted) return 'silent';
    // Hold silent until BOTH auth has resolved AND our watch-mount refetch
    // finished — no audio decision is made from stale/unknown state.
    if (authLoading) return 'silent';
    if (!prefReady) return 'silent';
    // Audio-blocked environment or runtime failure → downgrade to
    // silent-highlight so the viewer still gets the karaoke read.
    // Falling through to a would-be 'author' / 'system' branch would
    // just hang on `waitForAudioReady` and then either time out or
    // start a countdown followed by silence — a worse experience than
    // being upfront about the block.
    if (audioBlocked && wordCount > 0) return 'highlight_only';
    if (audioBlocked) return 'silent';
    // Signed-out viewer or user record without a stored pref → SILENT.
    // We do NOT speculatively play any audio. If they want narration they
    // can sign in and pick a positive preference in Settings.
    if (!narrationPref) return 'silent';
    switch (narrationPref) {
      case 'silent':
        return 'silent';
      case 'silent_highlight':
        // No audio, but drive the karaoke word highlighter from a
        // virtual clock (word timestamps if available, else a 2.5 wps
        // simulated pace). Countdown + highlight sequencing still runs.
        return 'highlight_only';
      case 'author_only':
        // Author's recorded voice only. NO system TTS fallback. When the
        // author didn't record anything, behave exactly like the
        // "No narration" setting — fully silent, no karaoke highlighting.
        return hasAudio ? 'author' : 'silent';
      case 'system_fallback':
        // Prefer the author's recorded voice; fall back to system TTS only
        // when the author didn't record.
        return hasAudio ? 'author' : 'system';
      default:
        // Unknown / future pref value we don't understand — DON'T guess,
        // stay silent. Better a quiet screen than a wrong sound.
        return 'silent';
    }
  }, [narrationPref, narrationMuted, hasAudio, authLoading, prefReady, audioBlocked, wordCount]);

  // `isSilent` gates AUDIO playback only — it's true for both fully-silent
  // and highlight-only modes since neither should play sound.
  const isSilent = narrationMode === 'silent' || narrationMode === 'highlight_only';

  // Highlighting runs whenever the mode isn't fully silent. `highlight_only`
  // opts in specifically so the viewer gets a visually-guided read even
  // without audio.
  const shouldHighlight = narrationMode !== 'silent' && wordCount > 0;
  const wordTs = useMemo(() => Array.isArray(prayer?.word_timestamps) ? prayer.word_timestamps : [], [prayer?.word_timestamps]);

  // Heartbeat participant tracking while inside the immersive overlay.
  // A periodic POST /participate refreshes the user's last_seen (15s freshness
  // window on the backend); a DELETE fires on close as a fast-path so the
  // count drops immediately for others.
  useEffect(() => {
    if (!praying || !prayer) return undefined;
    let stopped = false;
    const beat = () => { participatePrayer(prayer.id).catch(() => {}); };
    beat();
    heartbeatIntervalRef.current = setInterval(() => { if (!stopped) beat(); }, 5000);
    return () => {
      stopped = true;
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
      leavePrayer(prayer.id).catch(() => {});
    };
  }, [praying, prayer]);

  // Poll "Praying now" count while the overlay is open (every 5s).
  useEffect(() => {
    if (!praying || !prayer) {
      setParticipantsCount(0);
      setParticipantsList([]);
      setShowParticipantsList(false);
      return undefined;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const data = await getParticipants(prayer.id);
        if (cancelled) return;
        if (data && typeof data.count === 'number') setParticipantsCount(data.count);
        if (Array.isArray(data?.participants)) setParticipantsList(data.participants);
      } catch { /* silent */ }
    };
    tick();
    participantsIntervalRef.current = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      if (participantsIntervalRef.current) clearInterval(participantsIntervalRef.current);
    };
  }, [praying, prayer]);

  // Preview-page LIVE badge — polls the batch endpoint every 5s while the
  // WatchPrayer page is mounted (regardless of whether the immersive
  // overlay is open). Uses the same 15-second heartbeat window that the
  // home-page cards use, so the preview badge matches the feed badge
  // exactly. Independent of the overlay-only `participantsCount` above.
  const [previewLiveCount, setPreviewLiveCount] = useState(0);
  useEffect(() => {
    if (!prayer?.id) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const counts = await fetchLiveCounts([prayer.id]);
        if (cancelled) return;
        setPreviewLiveCount(Number(counts?.[prayer.id] || 0));
      } catch { /* silent — leave last known value */ }
    };
    tick();
    const interval = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [prayer?.id]);

  // When the audio finishes, scroll to the bottom of the overlay so the
  // Pray again (replay) button is fully in view.
  useEffect(() => {
    if (!playbackEnded) return;
    const el = overlayContentRef.current;
    if (!el) return;
    // Small delay so the replay button has been rendered before we scroll.
    const t = setTimeout(() => {
      try {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      } catch { el.scrollTop = el.scrollHeight; }
    }, 60);
    return () => clearTimeout(t);
  }, [playbackEnded]);

  // Scroll the active word into view (centered) inside the overlay content.
  // Disabled when the user has toggled the highlighter off — in that mode
  // they want to scroll freely without the karaoke synchronizer snapping
  // them back to the currently-narrated word.
  useEffect(() => {
    if (currentWordIndex < 0) return;
    if (highlightMuted) return;
    const el = wordRefs.current[currentWordIndex];
    if (el && overlayContentRef.current) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  }, [currentWordIndex, highlightMuted]);

  // Handle Escape + spacebar in overlay
  useEffect(() => {
    if (!praying) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closePraying();
      } else if (e.key === ' ' || e.code === 'Space') {
        // Only send hearts while the prayer is actually playing (not during countdown)
        if (countdown === 0) {
          e.preventDefault();
          spawnHeart();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [praying, countdown]);

  // Wait until the given <audio> element is ready to play through — i.e.
  // the browser has enough data buffered that a `play()` call won't
  // stall. Used as the STRICT gate before starting the countdown so
  // there's no window in which the audio is still being fetched/decoded
  // while the count is already ticking. Resolves on the first of:
  //   • `canplaythrough` (HAVE_ENOUGH_DATA)
  //   • `canplay`        (HAVE_FUTURE_DATA — safe on Safari where
  //                       canplaythrough is often delayed)
  //   • an `error` event (fail-open: proceed to countdown so the
  //                       viewer isn't stuck on the preparing modal
  //                       forever if the CDN hiccups)
  //   • an 8-second safety timeout (last-resort ceiling)
  const waitForAudioReady = (audioEl) => new Promise((resolve) => {
    if (!audioEl) return resolve();
    // readyState >= 3 (HAVE_FUTURE_DATA) already means we can start.
    if (audioEl.readyState >= 3) return resolve();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      audioEl.removeEventListener('canplaythrough', finish);
      audioEl.removeEventListener('canplay', finish);
      audioEl.removeEventListener('loadeddata', finish);
      audioEl.removeEventListener('error', finish);
      clearTimeout(safetyTimer);
      resolve();
    };
    audioEl.addEventListener('canplaythrough', finish, { once: true });
    audioEl.addEventListener('canplay', finish, { once: true });
    audioEl.addEventListener('loadeddata', finish, { once: true });
    audioEl.addEventListener('error', finish, { once: true });
    // Nudge the element to start loading if the browser hasn't already.
    try { audioEl.load(); } catch { /* ignore */ }
    const safetyTimer = setTimeout(finish, 8000);
  });

  // "Pray this now" flow — STRICT sequencing:
  //   1. Show the "Preparing the narration…" modal (setPreparingVoice).
  //   2. If system voice: `await` the TTS warmup fetch so the file is
  //      generated + cached server-side.
  //   3. Regardless of source (system OR author-recorded audio): `await`
  //      the <audio> element's canplaythrough/canplay event via
  //      `waitForAudioReady`. This is the STRICT gate — the countdown
  //      CANNOT start until this promise resolves.
  //   4. Only then call `startCountdown` (3 → 2 → 1).
  //   5. `onCountdownEnd` triggers playback on an already-ready element.
  // For silent viewers (author-only pref off + no audio_url, or session
  // mute) we skip the warmup entirely and go straight to the countdown —
  // no audio path to gate on.
  const handlePrayNow = async () => {
    if (preparingVoice || praying) return;

    // --- iOS AUDIO UNLOCK — SYNC, MUST run before any await ---
    // iOS Safari (and iOS-embedded WebViews) gate the tab's audio
    // subsystem behind an AudioContext.resume() that happens inside a
    // real user gesture. Since the rest of this function `await`s the
    // fresh-refetch and the audio-ready gate, we'd burn the gesture
    // window if we deferred this. So it runs on the very first line —
    // no promises, no async work between the tap and this call.
    //
    // The helper is a no-op on non-iOS platforms and idempotent within
    // a tab (uses sessionStorage), so this stays cheap on every tap
    // after the first.
    const wantsIOSUnlock = needsIOSUnlock();
    if (wantsIOSUnlock) {
      const unlockResult = unlockIOSAudioSync();
      // Fire-and-forget telemetry so the admin can see how many iOS
      // taps actually needed the unlock and how the unlock fared. We
      // only report the two non-happy outcomes to keep event volume
      // manageable — a successful unlock on iOS is expected and doesn't
      // need to fill the telemetry table.
      if (unlockResult === 'failed' || unlockResult === 'unavailable') {
        reportAudioFailure({
          reason: 'ios_unlock',
          label: unlockResult,
          prayer_id: id,
          phase: 'ios_unlock',
          narration_pref: user?.narration_preference,
        });
      }
    }

    // Prayed-count +1 the moment the viewer taps Pray Now Together.
    // Server dedupes within 30s so accidental double-taps stay at +1.
    bumpPrayedCount();

    // FRESH DATABASE READ — always re-fetch the prayer straight from
    // the backend at the moment the viewer taps "Pray Now Together".
    // Guarantees the immersive overlay uses the LATEST text, audio,
    // cover image, and voice-default flag even if the author edited
    // the prayer while this Watch page was already open. Cache-busted
    // via a rotating `_t` param + `Cache-Control: no-cache` header so
    // no browser / proxy / CDN layer can hand back a stale copy.
    //
    // Fallback: if the refetch itself fails (offline, 5xx), we keep
    // going with whatever's in state — better to pray with a slightly
    // stale copy than to block the interaction.
    let freshPrayer = prayer;
    try {
      const { data } = await api.get(`/prayers/${id}`, {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (data && data.id === id) {
        freshPrayer = data;
        setPrayer((prev) => (prev ? { ...prev, ...data } : data));
        if (typeof data.candles_count === 'number') {
          setCandlesCount(Number(data.candles_count) || 0);
        }
      }
    } catch (e) {
      console.error('pray-now fresh refetch failed, using cached state', e);
    }

    const needsNarrationGate =
      !isSilent &&
      (
        (narrationMode === 'system' && !!freshPrayer?.id) ||
        (narrationMode === 'author' && !!freshPrayer?.audio_url)
      );

    // --- SYNC: user-gesture audio prime ---
    // Chromium's autoplay policy grants a permission window only inside
    // the same synchronous task as the tap. If we `await` first, that
    // window is lost and the deferred play() at the end of the countdown
    // gets rejected. Prime the element (muted play → pause) NOW, before
    // any await, so the browser tags this <audio> as user-gesture
    // approved. Silent viewers skip the prime entirely.
    //
    // If the prime itself rejects with NotAllowedError (locked-down
    // browser, in-app WebView, iOS lockdown mode), route it through
    // the shared `handleAudioFailure` so `narrationMode` downgrades to
    // `highlight_only` — the countdown still runs, karaoke still
    // plays, we just don't try to play sound. Better than a countdown
    // followed by dead silence.
    if (!isSilent) {
      const audioEl = audioRef.current;
      if (audioEl) {
        try {
          audioEl.muted = true;
          audioEl.play()
            .then(() => audioEl.pause())
            .catch((err) => { handleAudioFailure('prime', err); });
        } catch (err) { handleAudioFailure('prime', err); }
      }
    }

    if (needsNarrationGate) {
      setPreparingVoice(true);
      try {
        // Step 2 — server-side warmup for system voice only. Author
        // recordings are already stored, no warmup needed.
        if (narrationMode === 'system' && freshPrayer?.id) {
          await fetch(`${BACKEND_URL}/api/prayers/${freshPrayer.id}/tts`, {
            credentials: 'include',
            cache: 'no-store',
          }).catch(() => { /* non-fatal — the <audio> gate below is authoritative */ });
        }
        // Step 3 — STRICT gate: wait for the audio element to signal
        // ready-to-play. Countdown cannot begin until this resolves.
        await waitForAudioReady(audioRef.current);
      } finally {
        setPreparingVoice(false);
      }
    }
    // Step 4 — narration is now guaranteed ready (or a fail-open path
    // was hit). Kick off the 3-2-1 countdown.
    startCountdown();
  };

  const startCountdown = () => {
    setPraying(true);
    setCountdown(3);
    setCurrentWordIndex(-1);
    setPlaybackEnded(false);

    // NOTE: audio priming used to live here but was hoisted into
    // `handlePrayNow` so it runs synchronously with the user gesture,
    // BEFORE the async narration-ready gate. See the block at the top
    // of handlePrayNow. Nothing to prime here anymore.

    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    countdownTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
          onCountdownEnd();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const onCountdownEnd = async () => {
    // The "@viewer prayed for you" notification is no longer fired here.
    // It is now fired alongside the `plays_count` bump inside
    // `bumpPrayedCount()` so both counters track the same explicit user
    // action (Pray Now Together or Pray again clicks) rather than a
    // background countdown completion.

    // HARD GATE first — no audio path can even be considered in silent mode.
    if (isSilent) {
      if (shouldHighlight) {
        // `highlight_only` mode: run the punctuation-aware silent
        // scheduler for a natural, meditative rhythm — 2s initial
        // breath after the countdown, base 110 WPM (545 ms/word),
        // with extra pauses at commas and periods.
        if (narrationMode === 'highlight_only') {
          startSilentHighlightSchedule();
        } else {
          setCurrentWordIndex(0);
        }
      }
      return;
    }

    // TTS warmup for system-voice viewers now happens in `handlePrayNow`
    // BEFORE the countdown starts, so by the time we reach this point the
    // narration audio is ready to play immediately. Nothing to do here
    // besides kicking off playback.

    const audioEl = audioRef.current;
    if (audioEl) {
      setTimeout(() => {
        // Re-check isSilent inside the timeout in case the pref flipped
        // during the delay (session mute toggle) OR audio was blocked
        // between countdown start and end.
        if (audioRef.current && !isSilent && !audioBlockedRef.current) {
          audioRef.current.muted = false;
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch((err) => { handleAudioFailure('countdown_end', err); });
        }
      }, 50);
    } else if (shouldHighlight) {
      startSimulatedPacing();
    }
    if (shouldHighlight) setCurrentWordIndex(0);
  };

  const replayPrayer = () => {
    // Prayed-count +1 for the Pray Again tap too. Same 30s server
    // dedupe guards against jitter, but a genuine replay after the
    // prayer finishes falls well outside the window.
    bumpPrayedCount();
    setPlaybackEnded(false);
    setCurrentWordIndex(0);
    if (simTimerRef.current) { clearTimeout(simTimerRef.current); clearInterval(simTimerRef.current); simTimerRef.current = null; }
    // HARD GATE — silent means silent on replay too.
    if (isSilent) {
      if (shouldHighlight) {
        if (narrationMode === 'highlight_only') {
          startSilentHighlightSchedule();
        } else {
          startSimulatedPacing();
        }
      }
      return;
    }
    const audioEl = audioRef.current;
    if (audioEl) {
      try {
        audioEl.muted = false;
        audioEl.currentTime = 0;
        audioEl.play().catch((err) => { handleAudioFailure('replay', err); });
      } catch (err) { handleAudioFailure('replay', err); }
    } else if (shouldHighlight) {
      startSimulatedPacing();
    }
  };

  // ---- Silent-highlight scheduler ----
  //
  //   The pace is derived from the prayer's own text — no fixed WPM,
  //   no user-facing tuning. `buildPaceProfile` inspects sentence
  //   length, poetic line-break density, syllable complexity, sacred
  //   / ALL-CAPS emphasis, and punctuation, then produces the exact
  //   per-word dwell in ms. See `buildPaceProfile` at module scope
  //   for the full derivation.
  //
  //   Only the initial 2-second breath after the 3-2-1 countdown is
  //   fixed — it's a contemplation pause, not a pace decision.
  const SILENT_HIGHLIGHT_INITIAL_DELAY_MS = 2000;
  const paceProfile = useMemo(
    () => buildPaceProfile(displayText || ''),
    [displayText]
  );

  const silentDelayAfter = (wordIdx) => {
    const d = paceProfile.wordDelays[wordIdx];
    return typeof d === 'number' && d > 0 ? d : 400;
  };

  // Punctuation-aware silent-highlight driver. Uses a recursive
  // `setTimeout` chain (not `setInterval`) so each hop can carry its
  // own delay based on the current word's trailing punctuation,
  // producing a natural breathing rhythm. Reuses `simTimerRef` as the
  // cancel handle so existing cleanup paths (unmount, mode change,
  // replay, close overlay) tear it down correctly.
  const startSilentHighlightSchedule = () => {
    if (simTimerRef.current) { clearTimeout(simTimerRef.current); clearInterval(simTimerRef.current); simTimerRef.current = null; }
    if (wordCount === 0) return;
    // Keep every word neutral through the 2-second initial breath so
    // there is no premature amber flash before the read actually starts.
    setCurrentWordIndex(-1);
    simTimerRef.current = setTimeout(() => {
      const advance = (idx) => {
        setCurrentWordIndex(idx);
        if (idx >= wordCount - 1) {
          simTimerRef.current = null;
          setPlaybackEnded(true);
          return;
        }
        const nextDelay = silentDelayAfter(idx);
        simTimerRef.current = setTimeout(() => advance(idx + 1), nextDelay);
      };
      advance(0);
    }, SILENT_HIGHLIGHT_INITIAL_DELAY_MS);
  };

  const startSimulatedPacing = (intervalMs) => {
    if (simTimerRef.current) { clearTimeout(simTimerRef.current); clearInterval(simTimerRef.current); simTimerRef.current = null; }
    // Default = 400 ms/word (2.5 wps). Callers can override with an
    // explicit interval.
    const stepMs = typeof intervalMs === 'number' && intervalMs > 0
      ? intervalMs
      : 1000 / 2.5;
    simTimerRef.current = setInterval(() => {
      setCurrentWordIndex((idx) => {
        if (idx >= wordCount - 1) {
          clearInterval(simTimerRef.current);
          simTimerRef.current = null;
          // Reveal the Replay affordance once the silent read finishes.
          setPlaybackEnded(true);
          return idx;
        }
        return idx + 1;
      });
    }, stepMs);
  };

  // ------------------------------------------------------------------
  // Karaoke-style word-highlight sync (recorded audio path).
  //
  // Why not just use `onTimeUpdate`?
  //   The <audio> element's `timeupdate` event is throttled to
  //   ~250-500ms in every modern browser. Whisper's word timestamps
  //   are far tighter (~150-400ms per word), so a naive `timeupdate`
  //   handler will miss words entirely and appear to lag behind the
  //   voice — worse the longer the recording runs.
  //
  // Strategy:
  //   * Poll `audio.currentTime` on every rAF frame (~60fps) while
  //     the audio is playing.
  //   * Look ahead ~120ms so the highlight leads the voice by one
  //     frame instead of trailing (karaoke UX convention).
  //   * Match `t` against each word's actual `[s, e]` interval; when
  //     `e` is missing, fall back to the *next word's* `s` so we
  //     always transition at the correct instant.
  //   * The rAF loop advances the pointer forward-only, so we don't
  //     re-scan the entire array on every frame — O(1) amortised.
  // ------------------------------------------------------------------
  const wordCursorRef = useRef(0);
  const LOOK_AHEAD_MS = 120;

  const stopWordSyncLoop = () => {
    if (rafIdRef.current != null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  };

  const runWordSyncFrame = () => {
    const audio = audioRef.current;
    if (!audio || wordCount === 0) { rafIdRef.current = null; return; }
    // Bail out cleanly if playback paused / ended between frames.
    if (audio.paused || audio.ended) { rafIdRef.current = null; return; }
    const t = audio.currentTime + LOOK_AHEAD_MS / 1000;

    if (wordTs.length > 0) {
      // Advance the cursor forward until we find the word whose
      // interval contains `t`. The cursor only moves forward, so
      // scrubbing backwards is handled by the seek handler below.
      let i = wordCursorRef.current;
      while (i < wordTs.length - 1) {
        const w = wordTs[i];
        const end = typeof w.e === 'number'
          ? w.e
          : (wordTs[i + 1] ? wordTs[i + 1].s : Infinity);
        if (t < end) break;
        i += 1;
      }
      wordCursorRef.current = i;
      const descIdx = wordTs.length === wordCount
        ? i
        : Math.min(wordCount - 1, Math.floor((i / wordTs.length) * wordCount));
      // Only setState when the value actually changes — React bails
      // out on identical values but skipping the call is even cheaper.
      setCurrentWordIndex((prev) => (prev === descIdx ? prev : descIdx));
    } else {
      // Fallback: uniform interpolation from total audio duration.
      const dur = audio.duration || prayer?.duration_sec || 0;
      if (dur > 0) {
        const idx = Math.min(wordCount - 1, Math.floor((t / dur) * wordCount));
        setCurrentWordIndex((prev) => (prev === idx ? prev : idx));
      }
    }

    rafIdRef.current = requestAnimationFrame(runWordSyncFrame);
  };

  const startWordSyncLoop = () => {
    stopWordSyncLoop();
    // Reset the forward-only cursor so re-play from the top always
    // starts scanning at word 0 (matches how audio playback resets).
    wordCursorRef.current = 0;
    rafIdRef.current = requestAnimationFrame(runWordSyncFrame);
  };

  const onAudioSeeked = () => {
    // Any manual seek (or programmatic `.currentTime = 0` on replay)
    // may go backwards, breaking the forward-only cursor. Rebuild the
    // cursor from the new position via a single scan, then let rAF
    // resume normal forward advancement.
    const audio = audioRef.current;
    if (!audio || wordTs.length === 0) return;
    const t = audio.currentTime;
    let i = 0;
    for (; i < wordTs.length - 1; i++) {
      const w = wordTs[i];
      const end = typeof w.e === 'number' ? w.e : wordTs[i + 1].s;
      if (t < end) break;
    }
    wordCursorRef.current = i;
    setCurrentWordIndex(
      wordTs.length === wordCount
        ? i
        : Math.min(wordCount - 1, Math.floor((i / wordTs.length) * wordCount))
    );
  };

  // Tear down the rAF loop on unmount / navigation so we don't leak.
  useEffect(() => () => stopWordSyncLoop(), []);

  const closePraying = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
    if (simTimerRef.current) { clearInterval(simTimerRef.current); simTimerRef.current = null; }
    stopWordSyncLoop();
    setPraying(false);
    setCountdown(0);
    setCurrentWordIndex(-1);
    setHearts([]);
    setPlaybackEnded(false);
    setHighlightMuted(false);
  };

  const spawnHeart = (originX = null) => {
    const id = ++heartIdRef.current;
    // Random-ish vertical starting offset so multiple hearts don't stack
    const bottom = 40 + Math.random() * 90;
    const wobble = (Math.random() - 0.5) * 20;
    // Hearts always rise from the right-hand margin, regardless of where the
    // user tapped. We keep the tap X only for future reference — for now we
    // let all hearts share the right-margin column so they read as a single
    // devotional stream instead of scattered dots.
    setHearts((prev) => [...prev, { id, bottom, wobble, originX }]);
    // Remove after animation completes
    setTimeout(() => {
      setHearts((prev) => prev.filter((h) => h.id !== id));
    }, 2200);
  };

  const handleOverlayTap = (e) => {
    if (!praying || countdown !== 0) return;
    // Extract the tap X so the heart flies from where the user touched.
    // Fall back gracefully for keyboard-triggered / synthetic events.
    let originX = null;
    if (e) {
      if (typeof e.clientX === 'number') originX = e.clientX;
      else if (e.touches && e.touches[0]) originX = e.touches[0].clientX;
      else if (e.changedTouches && e.changedTouches[0]) originX = e.changedTouches[0].clientX;
    }
    // Local optimistic heart for instant feedback — the SSE echo will
    // arrive ~100ms later via `handleIncomingHeart` and may add another,
    // which visually reads as a richer stream (intentional).
    spawnHeart(originX);
    // Broadcast to every other authenticated watcher of this prayer.
    // Fire-and-forget: rate-limit failures (throttled by the server at
    // 3s/user) are silently swallowed since the local heart already
    // gave the tapper their feedback. Anonymous users don't broadcast
    // (backend requires auth) — their local heart still shows.
    if (user && prayer?.id) {
      sendPrayerHeart(prayer.id).catch((err) => {
        console.warn('overlay tap send heart failed', err);
      });
    }
  };

  // SSE-driven heart from another watcher. Only spawns a visible heart
  // when the immersive overlay is active — otherwise the FloatingHearts
  // stage on the hero handles the animation.
  const prayingRef = useRef(false);
  useEffect(() => { prayingRef.current = praying; }, [praying]);
  const handleIncomingHeart = useCallback(() => {
    if (!prayingRef.current) return;
    // Randomise the origin X across the viewport width so incoming
    // hearts from other watchers feel like they're arriving from all
    // sides instead of a single column.
    const width = typeof window !== 'undefined' ? window.innerWidth : 400;
    const originX = Math.random() * width;
    spawnHeart(originX);
  }, []);

  // Real-time candle counter — fires whenever ANY watcher of this
  // prayer lights or extinguishes their candle. Reflects the fresh
  // server-side count so the immersive overlay's number ticks up/down
  // instantly without a re-fetch. Never trust the sign of the delta —
  // just take the authoritative count from the payload.
  const handleCandleUpdate = useCallback((payload) => {
    if (typeof payload?.candles_count !== 'number') return;
    setCandlesCount(payload.candles_count);
  }, []);

  // Space bar → send a heart during the immersive overlay. Uses the
  // same code path as an overlay tap so keyboard and touch converge.
  // Only active while the overlay is up and past the countdown — before
  // that, space would confuse the recording-oriented listeners.
  useEffect(() => {
    if (!praying || countdown !== 0) return undefined;
    const onKey = (e) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      // Ignore when the user is typing in an input somewhere behind
      // (defensive — overlay covers inputs but just in case).
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      e.preventDefault();
      handleOverlayTap(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [praying, countdown]);

  const handleShare = async () => {
    if (!prayer) return;
    // Fire the OS share sheet directly — no intermediate prompt.
    const result = await sharePrayer(prayer, user);
    if (result === 'cancelled') return;
    setShareStatus(result === 'error' ? 'error' : result);
    if (shareTimerRef.current) clearTimeout(shareTimerRef.current);
    shareTimerRef.current = setTimeout(() => setShareStatus(null), 1800);
  };

  const handleLightCandle = async () => {
    if (!prayer || candleLighting) return;
    if (!user) {
      // Not signed in — bounce to the login flow.
      window.location.href = '/login';
      return;
    }
    setCandleLighting(true);
    // Optimistic toggle. Snap to server-confirmed values on response;
    // roll both fields back on failure.
    const willLight = !isCandleLit;
    setIsCandleLit(willLight);
    setCandlesCount((n) => Math.max(0, n + (willLight ? 1 : -1)));
    try {
      const res = await lightCandle(prayer.id);
      if (res && typeof res.candles_count === 'number') {
        setCandlesCount(res.candles_count);
      }
      if (typeof res?.is_lit === 'boolean') {
        setIsCandleLit(res.is_lit);
      }
      // Fresh-light pulse cue (only when we JUST turned it on).
      if (res?.is_lit) {
        setCandleJustLit(true);
        if (candleTimerRef.current) clearTimeout(candleTimerRef.current);
        candleTimerRef.current = setTimeout(() => setCandleJustLit(false), 1500);
      } else {
        setCandleJustLit(false);
      }
    } catch (e) {
      console.error('toggle candle failed', e);
      // Roll back the optimistic toggle.
      setIsCandleLit(!willLight);
      setCandlesCount((n) => Math.max(0, n + (willLight ? -1 : 1)));
    } finally {
      setCandleLighting(false);
    }
  };

  useEffect(() => () => {
    if (shareTimerRef.current) clearTimeout(shareTimerRef.current);
    if (candleTimerRef.current) clearTimeout(candleTimerRef.current);
  }, []);

  // React to mid-playback mute toggling: pause audio when muted, resume when unmuted.
  useEffect(() => {
    if (!praying || countdown > 0) return;
    // HARD GATE — never touch play() in silent mode, regardless of how we
    // got here. This is the last line of defence.
    if (isSilent) {
      const el = audioRef.current;
      if (el) {
        try { el.pause(); } catch { /* ignore */ }
      }
      return;
    }
    const el = audioRef.current;
    if (!el) return;
    if (narrationMuted) {
      try { el.pause(); } catch { /* ignore */ }
    } else {
      try { el.play().catch((err) => { handleAudioFailure('unmute_toggle', err); }); } catch (err) { handleAudioFailure('unmute_toggle', err); }
    }
  }, [narrationMuted, praying, countdown, narrationMode, isSilent, handleAudioFailure]);

  if (loading) return <div className="p-10 text-center text-zinc-500 flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading prayer…</div>;
  if (error) return <div className="p-10 text-center text-zinc-500">{error}</div>;
  if (!prayer) return null;

  const isOwner = user && user.user_id === prayer.user_id;
  // Audio source resolution follows the narration mode EXACTLY:
  //   author → prayer.audio_url (recorded voice)
  //   system → /api/prayers/{id}/tts  (generated once, cached)
  //   silent → null (no audio element mounted at all, no TTS request fired)
  //
  // HARD GATE: even if some future change accidentally set audioSrc, the
  // `isSilent` short-circuit forces it back to null so no <audio> is ever
  // mounted and no /tts fetch is ever triggered when the user is silent.
  // This is the SINGLE point that decides whether audio can play.
  let audioSrc = null;
  if (!isSilent) {
    if (narrationMode === 'author' && prayer.audio_url) {
      audioSrc = `${BACKEND_URL}${prayer.audio_url}`;
    } else if (narrationMode === 'system') {
      audioSrc = `${BACKEND_URL}/api/prayers/${prayer.id}/tts`;
    }
  }
  const hasImage = Boolean(prayer.image_url);
  const imageSrc = hasImage ? `${BACKEND_URL}${prayer.image_url}` : null;
  // Video-first prayers: when the author recorded a clip via the
  // in-app video method, `video_url` points at the Cloudinary asset.
  // On the hero we swap the static cover image for a real `<video>`
  // player. The auto-extracted first-frame `image_url` still ships as
  // the video's poster so it looks identical to a cover-image prayer
  // until the viewer taps play.
  const hasVideo = Boolean(prayer.video_url);
  const videoSrc = hasVideo ? prayer.video_url : null;

  // Author-avatar overlay used in the top-left corner of the Hero /
  // Small author avatar — rendered inline to the LEFT of the @handle in
  // the author row below the hero (moved out of the hero overlay).
  const heroAuthorLabel = prayer.user_handle
    ? `@${prayer.user_handle}`
    : (prayer.user_name || 'Author');
  const authorAvatarSmall = (prayer.user_avatar || prayer.user_handle || prayer.user_name) ? (
    <span
      data-testid="watch-author-avatar"
      className="w-6 h-6 rounded-full overflow-hidden ring-1 ring-zinc-300 dark:ring-zinc-700 flex-shrink-0 inline-flex items-center justify-center"
    >
      {prayer.user_avatar ? (
        <img
          src={prayer.user_avatar}
          alt={heroAuthorLabel}
          className="w-full h-full object-cover"
        />
      ) : (
        <span className="w-full h-full flex items-center justify-center text-white text-[11px] font-semibold" style={{ background: 'rgba(0,0,0,0.55)' }}>
          {(prayer.user_handle || prayer.user_name || '?').slice(0, 1).toUpperCase()}
        </span>
      )}
    </span>
  ) : null;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 pnt-suppress-focus-ring" data-testid="watch-prayer-page">
      {/* Hidden narration audio element — MUST be mounted at the top of
          the tree (not inside the `{praying && ...}` overlay) so that
          `audioRef.current` is a real DOM node the moment the user taps
          "Pray this now". Two things depend on that:
            1. The synchronous user-gesture prime inside `handlePrayNow`
               (muted play → pause) which grants Chromium's autoplay
               permission for later playback after the countdown.
            2. The STRICT `waitForAudioReady` gate — with the element
               absent it resolved instantly and the countdown started
               before TTS was actually ready. */}
      {audioSrc && (
        <audio
          ref={audioRef}
          src={audioSrc}
          onPlay={startWordSyncLoop}
          onPause={stopWordSyncLoop}
          onSeeked={onAudioSeeked}
          onEnded={() => {
            stopWordSyncLoop();
            setCurrentWordIndex(wordCount - 1);
            setPlaybackEnded(true);
          }}
          className="hidden"
          data-testid="pray-audio"
          preload="auto"
        />
      )}
      {/* Preparing-voice modal — shown when the viewer clicks Pray Now and
          we're waiting on the TTS endpoint before the countdown begins. */}
      {preparingVoice && (
        <div
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
          data-testid="preparing-voice-modal"
          role="status"
          aria-live="polite"
        >
          <div className="max-w-sm w-full rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl px-6 py-7 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-950/60 flex items-center justify-center text-amber-600 dark:text-amber-300 mb-4">
              <Loader2 size={26} className="animate-spin" />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-1" data-testid="preparing-voice-title">
              Preparing the narration…
            </h3>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              We&apos;re generating the system-generated narration. This only takes a moment.
            </p>
          </div>
        </div>
      )}

      {/* Hero / Cover */}
      {hasVideo ? (
        <div
          className="relative rounded-xl overflow-hidden mb-4 bg-black aspect-video group"
          data-testid="watch-video-hero"
        >
          <video
            src={videoSrc}
            poster={imageSrc || undefined}
            controls
            playsInline
            preload="metadata"
            className="w-full h-full object-contain bg-black"
            data-testid="watch-video-player"
          />
          {/* Live floating-hearts receiver — pinned to the bottom-center
              of the hero, pointer-events-none so it never steals clicks.
              `hideSendButton` keeps the SSE subscription and rising-
              hearts animation live but drops the tap button (Watch page
              is view-only for hearts; sending happens elsewhere). */}
          {prayer?.id && (
            <div
              className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 z-20"
              data-testid="watch-floating-hearts-anchor"
            >
              <FloatingHearts prayerId={prayer.id} hideSendButton onIncomingHeart={handleIncomingHeart} onCandleUpdate={handleCandleUpdate} />
            </div>
          )}
          {previewLiveCount > 0 && (
            <div
              data-testid={`preview-live-badge-${prayer.id}`}
              title={`${previewLiveCount} praying right now`}
              className="absolute top-3 left-3 z-20 flex items-center gap-1.5 bg-rose-500/95 backdrop-blur text-white text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full shadow-lg"
            >
              <span className="relative flex h-2 w-2" aria-hidden="true">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              <span data-testid={`preview-live-badge-count-${prayer.id}`}>
                Live · {previewLiveCount}
              </span>
            </div>
          )}
        </div>
      ) : hasImage ? (
        <div className="relative rounded-xl overflow-hidden mb-4 bg-zinc-100 dark:bg-zinc-900 aspect-video group">
          <img
            src={imageSrc}
            alt={prayer.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              const img = e.currentTarget;
              if (!img.dataset.retried) {
                img.dataset.retried = '1';
                setTimeout(() => { img.src = `${imageSrc}?t=${Date.now()}`; }, 1500);
              }
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/20 pointer-events-none" />
          {/* Live floating-hearts receiver — pinned to the bottom-center
              of the hero, pointer-events-none so it never steals clicks.
              `hideSendButton` keeps the SSE subscription and rising-
              hearts animation live but drops the tap button (Watch page
              is view-only for hearts; sending happens elsewhere). */}
          {prayer?.id && (
            <div
              className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 z-20"
              data-testid="watch-floating-hearts-anchor"
            >
              <FloatingHearts prayerId={prayer.id} hideSendButton onIncomingHeart={handleIncomingHeart} onCandleUpdate={handleCandleUpdate} />
            </div>
          )}
          {previewLiveCount > 0 && (
            <div
              data-testid={`preview-live-badge-${prayer.id}`}
              title={`${previewLiveCount} praying right now`}
              className="absolute top-3 left-3 z-20 flex items-center gap-1.5 bg-rose-500/95 backdrop-blur text-white text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full shadow-lg"
            >
              <span className="relative flex h-2 w-2" aria-hidden="true">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              <span data-testid={`preview-live-badge-count-${prayer.id}`}>
                Live · {previewLiveCount}
              </span>
            </div>
          )}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6">
            <h2
              data-testid="hero-prayer-title"
              className="text-white text-center max-w-3xl px-3 py-1 rounded-md"
              style={{
                fontFamily: '"Times New Roman", Times, serif',
                fontSize: 'clamp(1.6rem, 4.2vw, 2.5rem)',
                lineHeight: 1.2,
                fontWeight: 600,
                textShadow: '0 2px 12px rgba(0,0,0,0.95), 0 0 6px rgba(0,0,0,0.9)',
              }}
            >
              {prayer.title}
            </h2>
            <button
              type="button"
              onClick={handlePrayNow}
              data-testid="pray-this-now-btn"
              className="mt-6 sm:mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/95 hover:bg-white text-zinc-900 font-medium shadow-lg backdrop-blur transition-transform hover:scale-105 active:scale-95"
              style={{ fontSize: '1.15rem', lineHeight: 1, letterSpacing: '0.01em' }}
            >
              <Play size={20} fill="currentColor" /> Pray Now Together
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden mb-4 aspect-video flex flex-col items-center justify-center p-8 gap-4 relative"
             style={{ background: bgGradient(prayer.background_color) }}>
          {/* Live floating-hearts receiver — pinned to the bottom-center
              of the hero, pointer-events-none so it never steals clicks.
              `hideSendButton` keeps the SSE subscription and rising-
              hearts animation live but drops the tap button (Watch page
              is view-only for hearts; sending happens elsewhere). */}
          {prayer?.id && (
            <div
              className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 z-20"
              data-testid="watch-floating-hearts-anchor"
            >
              <FloatingHearts prayerId={prayer.id} hideSendButton onIncomingHeart={handleIncomingHeart} onCandleUpdate={handleCandleUpdate} />
            </div>
          )}
          {previewLiveCount > 0 && (
            <div
              data-testid={`preview-live-badge-${prayer.id}`}
              title={`${previewLiveCount} praying right now`}
              className="absolute top-3 left-3 z-20 flex items-center gap-1.5 bg-rose-500/95 backdrop-blur text-white text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full shadow-lg"
            >
              <span className="relative flex h-2 w-2" aria-hidden="true">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              <span data-testid={`preview-live-badge-count-${prayer.id}`}>
                Live · {previewLiveCount}
              </span>
            </div>
          )}
          <div className="text-center" style={{ color: readableTextOn(prayer.background_color) }}>
            <div className="text-5xl mb-2" style={{ fontFamily: 'Caveat, cursive' }}>✧</div>
            <div
              data-testid="hero-prayer-title"
              className="text-2xl sm:text-3xl font-semibold"
              style={{
                fontFamily: '"Times New Roman", Times, serif',
                textShadow: readableTextShadow(prayer.background_color),
              }}
            >
              {prayer.title}
            </div>
          </div>
          <button
            type="button"
            onClick={handlePrayNow}
            data-testid="pray-this-now-btn"
            className="mt-6 sm:mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 font-medium shadow hover:scale-105 active:scale-95 transition-transform"
            style={{ fontSize: '1.05rem', lineHeight: 1, letterSpacing: '0.01em' }}
          >
            <Play size={18} fill="currentColor" /> Pray Now Together
          </button>
        </div>
      )}

      <h1 className="text-2xl font-bold mb-2 mt-6" data-testid="prayer-title">{prayer.title}</h1>

      {/* Author row — the candle-lighting button now sits at the far
          right next to Share, keeping the @handle as the row's primary
          anchor on the left. */}
      <div className="flex items-center gap-3 mb-6">
        {(() => {
          const authorDisplay = prayer.user_handle
            ? `@${prayer.user_handle}`
            : (prayer.user_name || 'Anonymous');
          return (
            <>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap relative z-10">
                  <span
                    role="link"
                    tabIndex={prayer.user_id ? 0 : -1}
                    onClick={(e) => {
                      if (!prayer.user_id) return;
                      e.stopPropagation();
                      navigate(`/circle/${prayer.user_id}`);
                    }}
                    onKeyDown={(e) => {
                      if (!prayer.user_id) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/circle/${prayer.user_id}`);
                      }
                    }}
                    className={`inline-flex items-center gap-1.5 text-sm font-medium ${prayer.user_id ? 'cursor-pointer hover:underline' : ''}`}
                    data-testid="watch-prayer-author"
                    aria-label={prayer.user_id ? `Open ${authorDisplay}'s prayer circle` : undefined}
                    title={prayer.user_id ? `View ${authorDisplay}'s prayer circle` : undefined}
                  >
                    {authorAvatarSmall}
                    {authorDisplay}
                  </span>
          </div>
          {/* Prominent metadata chip row — candle button + date + candles + views.
              The candle-lighting button sits at the far left on the SAME line
              as the date. Its box dimensions (padding + icon size) are FIXED,
              so lit/unlit state only swaps background/fill colors and applies
              a `transform`-based glow animation on the SVG — neither of which
              trigger layout reflow. Audio duration/plays stay in a secondary
              line below when audio is present. */}
          <div
            className="flex items-center gap-3 flex-wrap mt-1 text-sm font-semibold text-zinc-700 dark:text-zinc-300 tabular-nums"
            data-testid="watch-meta-row"
          >
            <button
              type="button"
              onClick={handleLightCandle}
              disabled={candleLighting}
              data-testid="light-candle-btn"
              data-candle-lit={isCandleLit ? 'true' : 'false'}
              className={`flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors active:scale-90 disabled:opacity-50 ${
                isCandleLit
                  ? 'bg-amber-100 dark:bg-amber-900/40 hover:bg-amber-200 dark:hover:bg-amber-900/60 text-amber-600'
                  : 'hover:bg-amber-50 dark:hover:bg-amber-950/40 text-amber-600 hover:text-amber-500'
              }`}
              aria-label={isCandleLit ? 'Unlight your candle' : 'Light a candle'}
              aria-pressed={isCandleLit}
              title={isCandleLit ? 'You lit this candle — tap to un-light' : 'Light a candle'}
            >
              <Flame
                size={20}
                strokeWidth={2.25}
                className={
                  candleJustLit
                    ? 'text-orange-500 candle-lit'
                    : isCandleLit
                      ? 'text-orange-500 candle-lit'
                      : 'text-amber-600 drop-shadow-[0_0_6px_rgba(251,146,60,0.55)]'
                }
                fill={
                  candleJustLit
                    ? 'rgb(249, 115, 22)'
                    : isCandleLit
                      ? 'rgba(249, 115, 22, 0.85)'
                      : 'rgba(251, 191, 36, 0.35)'
                }
              />
            </button>
            <button
              type="button"
              onClick={handleShare}
              data-testid="share-prayer-btn"
              className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-amber-50 dark:hover:bg-amber-950/40 text-amber-600 hover:text-amber-500 relative transition-colors active:scale-90"
              aria-label="Share prayer"
              title="Share prayer"
            >
              {shareStatus === 'copied' || shareStatus === 'shared'
                ? <Check size={20} strokeWidth={2.5} className="text-amber-600" />
                : <Share2 size={20} strokeWidth={2.25} className="text-amber-600 drop-shadow-[0_0_6px_rgba(251,146,60,0.55)]" />
              }
              {shareStatus === 'copied' && (
                <span
                  data-testid="share-toast"
                  className="absolute -bottom-8 left-0 whitespace-nowrap text-[11px] px-2 py-1 rounded-md bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 shadow"
                >
                  Link copied
                </span>
              )}
              {shareStatus === 'error' && (
                <span
                  data-testid="share-error"
                  className="absolute -bottom-8 left-0 whitespace-nowrap text-[11px] px-2 py-1 rounded-md bg-red-600 text-white shadow"
                >
                  Couldn&apos;t share
                </span>
              )}
            </button>
            <span className="inline-flex items-center gap-1.5" data-testid="watch-meta-date"
                  title={new Date(prayer.created_at).toLocaleString()}>
              <Calendar size={15} strokeWidth={2.25} className="text-zinc-500" />
              {fmtLongDate(prayer.created_at)}
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="candles-count-caption"
                  title={`${candlesCount} candles lit`}>
              <Flame size={15} strokeWidth={2.25} className="text-amber-600" fill="rgba(251, 191, 36, 0.35)" />
              <span>{fmtCount(candlesCount)}</span>
              <span className="font-medium text-zinc-500 dark:text-zinc-400">
                {candlesCount === 1 ? 'candle' : 'candles'}
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="watch-meta-views"
                  title={`${prayer.plays_count || 0} views`}>
              <Eye size={15} strokeWidth={2.25} className="text-zinc-500" />
              <span>{fmtCount(prayer.plays_count)}</span>
              <span className="font-medium text-zinc-500 dark:text-zinc-400">
                {(prayer.plays_count || 0) === 1 ? 'view' : 'views'}
              </span>
            </span>
          </div>
          {hasAudio && (
            <div className="text-xs text-zinc-500 mt-1" data-testid="watch-meta-audio">
              {fmtDur(prayer.duration_sec)} audio
            </div>
          )}
        </div>
            </>
          );
        })()}
        <ReportPrayerButton prayerId={prayer.id} variant="inline" isOwner={Boolean(isOwner)} />
      </div>

      {/* Hashtag pills removed from the preview page — hashtags are
          still returned by the API and auto-generated on submit so
          they persist for analytics/discovery, but no user-facing
          view surfaces them anymore. */}

      {/* Owner comments — replaces the old prayer-description block on the
          preview page per product spec. Read-only here; management happens
          via the OwnerCommentsDialog opened from the card / manage pages. */}
      <div className="mb-8" data-testid="preview-owner-comments">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800 dark:text-zinc-200 mb-3">
          <MessageCircle size={16} className="text-amber-500" />
          <span>Owner comments</span>
          <span className="text-xs font-normal text-zinc-500 tabular-nums">
            {ownerCommentsLoading ? '' : `${ownerComments.length}`}
          </span>
        </div>
        {ownerCommentsLoading && (
          <div className="text-sm text-zinc-500 flex items-center gap-2" data-testid="preview-owner-comments-loading">
            <Loader2 className="animate-spin" size={14} /> Loading comments…
          </div>
        )}
        {!ownerCommentsLoading && ownerComments.length === 0 && (
          <div className="text-sm text-zinc-500 italic" data-testid="preview-owner-comments-empty">
            The prayer owner hasn&apos;t left any comments yet.
          </div>
        )}
        {!ownerCommentsLoading && ownerComments.length > 0 && (
          <ul className="space-y-3" data-testid="preview-owner-comments-list">
            {ownerComments.map((c) => (
              <li
                key={c.comment_id}
                className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50/60 dark:bg-zinc-900/60 p-3"
                data-testid={`preview-owner-comment-${c.comment_id}`}
              >
                <div className="text-sm whitespace-pre-wrap break-words">{c.text}</div>
                <div className="mt-1.5 text-[11px] text-zinc-500 tabular-nums">
                  {new Date(c.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {displayText && (
        <div
          className="mt-2 mb-8 text-[15px] leading-relaxed text-zinc-800 dark:text-zinc-200 whitespace-pre-wrap break-words"
          data-testid="preview-prayer-text"
          style={{ fontFamily: '"Times New Roman", Times, serif' }}
        >
          {displayText}
        </div>
      )}

      {/* Immersive "Pray This Now" overlay */}
      {praying && (
        <div
          className="fixed inset-0 z-50 select-none"
          data-testid="pray-overlay"
          onClick={handleOverlayTap}
          onTouchStart={handleOverlayTap}
        >
          {/* Background image (dimmed) or fallback gradient */}
          {hasImage ? (
            <div className="absolute inset-0">
              <img src={imageSrc} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px]" />
            </div>
          ) : (
            <div
              className="absolute inset-0"
              style={{ background: bgGradient(prayer.background_color) }}
            />
          )}

          {/* Floating light effect between the cover image and the prayer
              text. Style is a tryout (cycle button / ?effect= param);
              speed remains the admin-managed site setting. */}
          <ImmersiveEffect style={effectStyle} speed={sparksSpeed} />

          {/* Silent-mode notice — surfaces the audio downgrade reason so
              the viewer understands WHY there's no narration. Only rendered
              when the environment / runtime blocked audio (in-app WebView,
              data saver, play() rejection). Not shown for a plain 'silent'
              preference — that's a user choice, not a failure. */}
          {audioBlocked && audioBlockReason && (
            <div
              data-testid="immersive-silent-mode-badge"
              className="absolute top-16 left-1/2 -translate-x-1/2 z-30 max-w-[90%] px-3.5 py-2 rounded-full bg-black/55 backdrop-blur-md text-white/90 text-xs sm:text-sm flex items-center gap-2 shadow-lg pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <VolumeX size={14} className="opacity-80 shrink-0" />
              <span className="truncate">
                Silent mode
                {audioBlockReason.label ? ` — ${audioBlockReason.label} blocks audio` : ''}
              </span>
              {audioBlockReason.reason === 'in_app_webview' && audioBlockReason.openInBrowserHref && (
                <a
                  href={audioBlockReason.openInBrowserHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-in-browser-link"
                  className="ml-1 underline underline-offset-2 hover:text-white"
                  onClick={(e) => e.stopPropagation()}
                >
                  Open in browser
                </a>
              )}
            </div>
          )}

          {/* Top-right controls: mute + close.
              The mute button is hidden when there's no narration for the
              viewer to mute — specifically when:
                1. the viewer's preference is "silent" (never narrated), OR
                2. the preference is "author only" and there is no usable
                   author recording (either the owner didn't record, or
                   they turned OFF "Use my voice recording for narration"
                   which flips `hasAudio` to false).
              These cases already resolve to narrationMode === 'silent',
              so the mute toggle would be a no-op — hide it instead. */}
          <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
            {shouldHighlight && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setHighlightMuted(m => !m); }}
                data-testid="toggle-highlight-btn"
                aria-pressed={!highlightMuted}
                aria-label={highlightMuted ? 'Turn word highlighting on' : 'Turn word highlighting off'}
                title={highlightMuted ? 'Turn word highlighting on' : 'Turn word highlighting off'}
                className="w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur transition-colors"
              >
                {highlightMuted
                  ? <Type size={20} />
                  : <Highlighter size={20} />}
              </button>
            )}
            {!(
              narrationPref === 'silent'
              || (narrationPref === 'author_only' && !hasAudio)
            ) && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setNarrationMuted(m => !m); }}
                data-testid="mute-narration-btn"
                aria-label={narrationMuted ? 'Turn narration on' : 'Turn narration off'}
                title={narrationMuted ? 'Turn narration on' : 'Turn narration off'}
                className="w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur transition-colors"
              >
                {narrationMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </button>
            )}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); closePraying(); }}
              data-testid="close-pray-btn"
              aria-label="Close prayer view"
              className="w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Effect-style tryout button — ADMIN ONLY. Cycles Sparks →
              Spark Trails → Rising Orbs → Light Beams → Ripples →
              Candles → No Effect. */}
          {user?.role === 'admin' && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); cycleEffect(); }}
              data-testid="effect-cycle-btn"
              title="Try a different light effect"
              className="absolute bottom-4 left-4 z-20 h-8 px-3 rounded-full bg-black/35 hover:bg-black/55 text-white/80 hover:text-white text-xs font-medium backdrop-blur flex items-center gap-1.5 transition-colors"
            >
              <Sparkles size={13} />
              {EFFECT_LABELS[effectStyle]}
            </button>
          )}

          {/* Praying with you now — live participant count, upper-left corner */}
          <div
            className="absolute top-4 left-4 z-20 flex flex-col items-start gap-2"
            data-testid="participants-section"
          >
            <button
              type="button"
              onClick={() => setShowParticipantsList((v) => !v)}
              data-testid="participants-count-toggle"
              aria-expanded={showParticipantsList}
              aria-controls="participants-list"
              title={showParticipantsList ? 'Hide list of who\'s praying' : 'Show who\'s praying now'}
              className="flex items-center gap-2 rounded-full bg-black/45 backdrop-blur text-white pl-1 pr-3 py-1 text-xs sm:text-sm shadow-lg hover:bg-black/60 transition-colors active:scale-95 cursor-pointer"
            >
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  data-testid="participants-user-avatar"
                  className="w-7 h-7 rounded-full object-cover ring-1 ring-white/40"
                />
              ) : (
                <div
                  data-testid="participants-user-avatar-fallback"
                  className="w-7 h-7 rounded-full bg-amber-500/70 ring-1 ring-white/40 flex items-center justify-center text-[10px] font-semibold"
                >
                  {(user?.name || 'P')[0].toUpperCase()}
                </div>
              )}
              <div
                data-testid="participants-count-badge"
                className="relative overflow-hidden flex items-center justify-center min-w-[2rem] h-8 px-1.5 rounded-full bg-purple-900 text-white ring-2 ring-purple-800/70 shadow-inner"
                style={{ animation: 'prayCountPulse 2.4s ease-in-out infinite' }}
              >
                {/* Diagonal sheen that slides across every 2.6s */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  aria-hidden="true"
                  style={{
                    background:
                      'linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.85) 48%, rgba(255,255,255,0.85) 52%, transparent 70%)',
                    animation: 'prayCountShine 2.6s ease-in-out infinite',
                    mixBlendMode: 'screen',
                  }}
                />
                <span
                  className="relative tabular-nums font-extrabold leading-none text-base sm:text-lg"
                  data-testid="participants-count"
                >
                  {participantsCount}
                </span>
              </div>
              <span className="font-medium">online praying this now</span>
              <ChevronRight
                size={14}
                strokeWidth={2.5}
                data-testid="participants-twistie"
                className={`transition-transform duration-200 text-white/85 ${showParticipantsList ? 'rotate-90' : 'rotate-0'}`}
                aria-hidden="true"
              />
            </button>
            {/* Share button — directly under the participants pill */}
            <button
              type="button"
              onClick={handleShare}
              data-testid="share-prayer-overlay-btn"
              aria-label="Share prayer"
              title="Share prayer"
              className="pointer-events-auto flex items-center gap-1.5 rounded-full bg-black/45 hover:bg-black/60 backdrop-blur text-white pl-2 pr-3 py-1 text-xs sm:text-sm shadow-lg transition-colors active:scale-95"
            >
              {shareStatus === 'copied' || shareStatus === 'shared' ? (
                <>
                  <Check size={14} className="text-amber-300" />
                  <span className="font-medium">
                    {shareStatus === 'shared' ? 'Shared' : 'Link copied'}
                  </span>
                </>
              ) : shareStatus === 'error' ? (
                <>
                  <Share2 size={14} className="text-red-300" />
                  <span className="font-medium">Couldn&apos;t share</span>
                </>
              ) : (
                <>
                  <Share2 size={14} className="text-amber-300" />
                  <span className="font-medium">Share prayer</span>
                </>
              )}
            </button>
            {/* Light a candle — persistent devotional action, separate from ephemeral heart taps */}
            <button
              type="button"
              onClick={handleLightCandle}
              disabled={candleLighting}
              data-testid="light-candle-overlay-btn"
              aria-label="Light a candle for this prayer"
              title="Light a candle"
              className="pointer-events-auto flex items-center gap-1.5 rounded-full bg-black/45 hover:bg-black/60 backdrop-blur text-white pl-2 pr-3 py-1 text-xs sm:text-sm shadow-lg transition-colors active:scale-95 disabled:opacity-60"
            >
              <Flame
                size={14}
                className={
                  candleJustLit || isCandleLit
                    ? 'text-orange-400 candle-lit'
                    : 'text-amber-300'
                }
                fill={candleJustLit || isCandleLit ? 'rgb(251, 146, 60)' : 'none'}
              />
              <span className="font-medium">Light a candle</span>
              <span
                className="tabular-nums font-bold text-amber-300"
                data-testid="candles-count-overlay"
              >
                {candlesCount}
              </span>
            </button>

            {/* Participants list — toggled via the count pill above. Sits
                directly under the light-a-candle button so it flows down
                the left column. Each name is a link that opens the search
                results for that user's handle in a new window/tab. */}
            {showParticipantsList && (
              <div
                id="participants-list"
                data-testid="participants-list"
                className="pointer-events-auto max-w-[280px] w-64 rounded-2xl bg-black/60 backdrop-blur text-white shadow-lg ring-1 ring-white/10 p-2 flex flex-col gap-0.5"
              >
                <div className="px-2 py-1 text-[11px] uppercase tracking-wide text-white/60 font-semibold flex items-center justify-between">
                  <span>Praying now</span>
                  <span
                    className="tabular-nums text-white/50"
                    data-testid="participants-list-header-count"
                  >
                    {participantsCount}
                  </span>
                </div>
                {(() => {
                  // Anonymous participants (no handle to show) — surface a
                  // dedicated row so the list total always matches the
                  // count pill and updates whenever the poll runs.
                  const anonCount = Math.max(0, participantsCount - participantsList.length);
                  if (participantsList.length === 0 && anonCount === 0) {
                    return (
                      <div
                        data-testid="participants-list-empty"
                        className="px-2 py-2 text-xs text-white/60"
                      >
                        No one else here yet — keep praying.
                      </div>
                    );
                  }
                  return (
                    <>
                      {participantsList.map((p) => {
                    const isMe = user && p.user_id === user.user_id;
                    // Always render an `@handle`. Users without a saved
                    // handle fall back to a slug of their name so the
                    // list stays consistent (no bare names).
                    const handle = p.handle
                      || (p.name ? p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') : '');
                    const label = handle ? `@${handle}` : '@anonymous';
                    const query = handle || p.name;
                    const slug = (p.user_id || handle || label).replace(/[^a-zA-Z0-9]+/g, '-');
                    return (
                      <button
                        key={p.user_id || label}
                        type="button"
                        data-testid={`participant-item-${slug}`}
                        data-is-me={isMe ? 'true' : 'false'}
                        onClick={() => {
                          if (!query) return;
                          const target = `/?q=${encodeURIComponent(query)}`;
                          window.open(target, '_blank', 'noopener,noreferrer');
                        }}
                        title={isMe ? 'This is you — search for your prayers' : `Search for prayers by ${label}`}
                        className={`flex items-center gap-2 rounded-lg outline-none px-2 py-1.5 text-left transition-colors ${
                          isMe
                            ? 'bg-amber-500/25 ring-1 ring-amber-400/60 hover:bg-amber-500/35 focus:bg-amber-500/35'
                            : 'hover:bg-white/10 focus:bg-white/10'
                        }`}
                      >
                        {p.picture ? (
                          <img
                            src={p.picture}
                            alt=""
                            className={`w-6 h-6 rounded-full object-cover shrink-0 ${isMe ? 'ring-1 ring-amber-300' : 'ring-1 ring-white/30'}`}
                          />
                        ) : (
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 ${
                            isMe
                              ? 'bg-amber-300 text-amber-900 ring-1 ring-amber-200'
                              : 'bg-amber-500/70 ring-1 ring-white/30'
                          }`}>
                            {(p.handle || p.name || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <span className={`text-sm font-medium truncate ${
                          isMe
                            ? 'text-amber-100'
                            : 'text-amber-200 hover:text-amber-100'
                        }`}>
                          {label}
                        </span>
                        {isMe && (
                          <span
                            data-testid="participant-you-tag"
                            className="ml-auto text-[10px] uppercase tracking-wide font-bold text-amber-950 bg-amber-300 rounded-full px-1.5 py-0.5 shrink-0"
                          >
                            you
                          </span>
                        )}
                      </button>
                    );
                      })}
                      {anonCount > 0 && (
                        <div
                          data-testid="participants-list-anon"
                          className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-white/70"
                        >
                          <div className="w-6 h-6 rounded-full bg-white/10 ring-1 ring-white/20 flex items-center justify-center text-[10px] font-semibold shrink-0">
                            ?
                          </div>
                          <span>
                            {anonCount === 1
                              ? '1 anonymous viewer praying'
                              : `${anonCount} anonymous viewers praying`}
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Audio element moved OUT of the `{praying && ...}` block —
              see the top of the return() for the hoisted mount. Keeping
              it here previously meant `audioRef.current` was null at the
              moment the user tapped "Pray this now", which (a) skipped
              the synchronous user-gesture prime that Chromium's autoplay
              policy requires, and (b) resolved `waitForAudioReady`
              instantly with a null element — the countdown started
              before TTS was actually ready. */}

          {/* Countdown */}
          {countdown > 0 && (
            <div className="absolute inset-0 flex items-center justify-center z-10" data-testid="pray-countdown">
              <div
                key={countdown}
                className="text-white font-bold tabular-nums"
                style={{
                  fontSize: 'clamp(6rem, 20vw, 12rem)',
                  textShadow: '0 4px 32px rgba(0,0,0,0.95), 0 0 20px rgba(255,220,130,0.5)',
                  animation: 'prayCountdownPop 1s ease-out',
                }}
              >
                {countdown}
              </div>
            </div>
          )}

          {/* Prayer content — shown after countdown */}
          {countdown === 0 && (
            <div
              ref={overlayContentRef}
              // Top padding must clear the entire upper-left overlay stack
              // (participants pill → Share button → Light-a-candle button),
              // which is ~128px tall on narrow viewports. Use pt-36 on
              // mobile so nothing overlaps, then tighten slightly on wider
              // screens where the layout has more horizontal room.
              className="absolute inset-0 z-10 flex flex-col items-center justify-start px-6 pt-40 sm:pt-32 lg:pt-24 pb-28 overflow-y-auto"
              data-testid="pray-overlay-content"
            >
              {/* ONE unified highlight box wrapping the ENTIRE prayer —
                  title + body render inside. Skips entirely when there is
                  neither a title nor a description to show, so we never
                  render an empty white pill on a blank prayer. */}
              {(prayer.title || displayText) && (
                <div
                  data-testid="prayer-highlight-box"
                  className="max-w-2xl w-full flex flex-col items-center"
                  style={PRAYER_HIGHLIGHT_BOX_STYLE}
                >
                  {prayer.title && (
                    <h2
                      className="text-center mb-4"
                      style={{
                        fontFamily: '"Times New Roman", Times, serif',
                        fontSize: 'clamp(1.6rem, 4.2vw, 2.5rem)',
                        lineHeight: 1.25,
                        fontWeight: 600,
                        // Gentle fade-in so the title materialises rather
                        // than snapping onto the screen the instant the
                        // countdown ends. Runs once thanks to `both`.
                        animation: 'prayContentFadeIn 3000ms ease-out both',
                        // Inherits color + textShadow from the outer box so
                        // the visual treatment stays unified.
                      }}
                    >
                      {prayer.title}
                    </h2>
                  )}
                  {displayText && (
                    <div
                      className="w-full"
                      style={{
                        // Slightly delayed fade so the body settles in
                        // AFTER the title has established itself — subtle
                        // stagger, not a jarring cascade.
                        animation: 'prayContentFadeIn 3000ms ease-out 350ms both',
                      }}
                    >
                      <PoemText
                        text={displayText}
                        currentWordIndex={shouldHighlight && !highlightMuted ? currentWordIndex : -1}
                        wordRefs={wordRefs}
                      />
                    </div>
                  )}
                </div>
              )}
              {!displayText && !prayer.title && (
                <p className="text-white/85 italic text-center" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.95)' }}>
                  Sit quietly with this prayer.
                </p>
              )}

              {playbackEnded && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); replayPrayer(); }}
                  data-testid="replay-prayer-btn"
                  className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/95 hover:bg-white text-zinc-900 font-medium shadow-lg backdrop-blur transition-transform hover:scale-105 active:scale-95"
                  style={{ fontSize: '1.05rem', lineHeight: 1, letterSpacing: '0.01em' }}
                >
                  <RotateCcw size={20} /> Pray again
                </button>
              )}
            </div>
          )}

          {/* Floating rising hearts — anchored to the right margin */}
          <div className="pointer-events-none absolute right-3 sm:right-5 bottom-0 top-0 z-30 w-14" data-testid="hearts-container">
            {hearts.map((h) => (
              <div
                key={h.id}
                data-testid="floating-heart"
                className="absolute right-0"
                style={{
                  bottom: `${h.bottom}px`,
                  '--tx': `${h.wobble}px`,
                  animation: 'prayHeartRise 2.2s cubic-bezier(0.2, 0.6, 0.3, 1) forwards',
                }}
              >
                <Heart
                  size={20}
                  fill="rgb(244, 63, 94)"
                  strokeWidth={0}
                  style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.6))' }}
                />
              </div>
            ))}
          </div>

          {/* Inline keyframes for countdown pop + heart rise */}
          <style>{`
            @keyframes prayCountdownPop {
              0% { transform: scale(0.85); opacity: 0; }
              25% { transform: scale(1.08); opacity: 1; }
              80% { transform: scale(1); opacity: 1; }
              100% { transform: scale(0.95); opacity: 0; }
            }
            @keyframes prayHeartRise {
              0% { transform: translate(var(--tx, 0), 0) scale(0.6); opacity: 0; }
              20% { opacity: 1; transform: translate(var(--tx, 0), -30px) scale(1.1); }
              70% { opacity: 1; }
              100% { transform: translate(var(--tx, 0), -300px) scale(0.9); opacity: 0; }
            }
            @keyframes prayCountShine {
              0%   { transform: translateX(-140%); }
              55%  { transform: translateX(140%); }
              100% { transform: translateX(140%); }
            }
            @keyframes prayCountPulse {
              0%, 100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.55), inset 0 1px 2px rgba(0,0,0,0.25); }
              50%      { box-shadow: 0 0 0 6px rgba(251, 191, 36, 0.0),  inset 0 1px 2px rgba(0,0,0,0.25); }
            }
            @keyframes prayContentFadeIn {
              0%   { opacity: 0; transform: translateY(6px); }
              100% { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default WatchPrayer;
